import type { CodHandover, DriverShipment } from "@khazzin/api-client";
import type { OutboxEntry } from "@khazzin/api-client/sync";
import { type CashRow, COLLECT } from "../cod/cod";

/**
 * The day-end handover's rules (EP10-S20), apart from the screen so a test
 * can read them: the day's tallies, the per-currency cards, what blocks a
 * handover, and where the latest handover stands. The server picks the
 * collections and sums them; these figures are what the driver sees first.
 */

/** The driver's handovers in the query cache; the stream's hint re-reads them (Shell). */
export const HANDOVERS_KEY = ["driver", "handovers"] as const;

/** The day's stops, counted for the summary. */
export interface Tallies {
	delivered: number;
	partial: number;
	failed: number;
	pickups: number;
	/** Every command still waiting on the phone, not only cash. */
	queued: number;
}

/** A delivery attempt that did not deliver: failed, or failed and rescheduled. */
const FAILED = ["delivery_failed", "rescheduled", "failed"];

export function tallies(
	shipments: Record<string, DriverShipment>,
	outbox: readonly OutboxEntry[],
): Tallies {
	const out: Tallies = { delivered: 0, partial: 0, failed: 0, pickups: 0, queued: 0 };
	for (const s of Object.values(shipments)) {
		if (s.kind === "pickup") {
			out.pickups += s.status === "collected" ? 1 : 0;
		} else if (s.status === "delivered") {
			out.delivered++;
		} else if (s.status === "partially_delivered") {
			out.partial++;
		}
		out.failed += FAILED.includes(s.status) ? 1 : 0;
	}
	out.queued = outbox.filter((e) => e.state === "queued").length;
	return out;
}

/** One currency's card: what was due, what the driver took, and the difference. */
export interface CurrencyCard {
	currency: string;
	expectedMinor: number;
	collectedMinor: number;
	varianceMinor: number;
	count: number;
}

/** The Cash tab's rows (EP10-S19), summed per currency. */
export function currencyCards(rows: readonly CashRow[]): CurrencyCard[] {
	const cards = new Map<string, CurrencyCard>();
	for (const r of rows) {
		const card = cards.get(r.currency) ?? {
			currency: r.currency,
			expectedMinor: 0,
			collectedMinor: 0,
			varianceMinor: 0,
			count: 0,
		};
		card.expectedMinor += r.expectedMinor;
		card.collectedMinor += r.collectedMinor;
		card.varianceMinor = card.collectedMinor - card.expectedMinor;
		card.count++;
		cards.set(r.currency, card);
	}
	return [...cards.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/** Cash confirmations still queued: the `queuedCodCount` the server refuses above zero. */
export function queuedCod(outbox: readonly OutboxEntry[]): number {
	return outbox.filter((e) => e.state === "queued" && e.command === COLLECT).length;
}

/** Where a handover stands on the driver's screen. */
export type Phase = "waiting" | "confirmed" | "disputed";

export function phaseOf(h: Pick<CodHandover, "status">): Phase {
	switch (h.status) {
		case "closed":
			return "confirmed";
		case "disputed":
			return "disputed";
		default:
			return "waiting";
	}
}

/** Why Start handover is disabled, or `ok`. */
export type StartGate = "ok" | "queued" | "offline" | "empty" | "open";

/**
 * Start handover needs no queued cash (the server's `unsynced_collections`),
 * a signal (the batch is online only), cash that has synced, and no handover
 * already waiting at the desk (`handover_open`).
 */
export function startGate(
	queued: number,
	online: boolean,
	syncedRows: number,
	open: boolean,
): StartGate {
	switch (true) {
		case queued > 0:
			return "queued";
		case !online:
			return "offline";
		case open:
			return "open";
		case syncedRows === 0:
			return "empty";
		default:
			return "ok";
	}
}
